import {
  BEER_INGESTION_STATUS_FILTER_ALL,
  BEER_INGESTION_TRUST_TARGET_PERCENT,
} from '../callableBeerConstants';
import { isIngestionPipeSource } from './breweryBeerSourceFilters';
import {
  BEER_INGESTION_MONITORING_FILTER_ALL,
  matchesBeerIngestionMonitoringFilter,
} from './beerIngestionMonitoring';

const UNTRUSTABLE_STRUCTURES = new Set(['image-menu', 'unsupported']);

export const EMPTY_BEER_INGESTION_SUMMARY = {
  totalInScope: 0,
  trusted: 0,
  needsSource: 0,
  noSuitableSource: 0,
  excluded: 0,
  trustedPercent: 0,
  increment1ExceptionCount: 0,
  increment1Denominator: 0,
};

export function formatKebabLabel(value) {
  return String(value || '')
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatSnakeLabel(value) {
  return String(value || '')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatBeerIngestionStatusLabel(status) {
  if (status === 'needs_source') {
    return 'Needs source';
  }
  if (status === 'no_suitable_source') {
    return 'No suitable source';
  }
  return formatSnakeLabel(status);
}

export function formatNoSuitableSourceReasonLabel(reason) {
  return formatSnakeLabel(reason);
}

export function formatBeerIngestionSourceChipLabel(source = {}) {
  const categoryProps = getBeerSourceCategoryChipProps(source.sourceCategory);
  const hostname = formatUrlHostname(source.url);
  const primary = hostname || categoryProps.label;
  const structure = formatKebabLabel(source.sourceStructure);
  if (!structure) {
    return primary;
  }
  return `${primary} · ${structure}`;
}

export function formatBeerIngestionSourceChipAriaLabel(source = {}) {
  const label = formatBeerIngestionSourceChipLabel(source);
  return source.isTrusted ? `${label} (trusted)` : label;
}

export function formatNoSuitableSourceHoverText(classification) {
  const reasonCode =
    classification?.noSuitableSourceReason || classification?.reason;
  const reasonLabel = formatNoSuitableSourceReasonLabel(reasonCode);
  const notes =
    typeof classification?.notes === 'string' ? classification.notes.trim() : '';
  if (reasonLabel && notes) {
    return `${reasonLabel} — ${notes}`;
  }
  return reasonLabel || notes;
}

export function getBeerSourceTrustBlockReason(source) {
  if (!source) {
    return 'Add a source first';
  }
  if (source.sourceCategory === 'aggregator') {
    return 'Aggregator sources cannot be marked trusted';
  }
  if (UNTRUSTABLE_STRUCTURES.has(source.sourceStructure)) {
    return `Sources with structure ${source.sourceStructure} cannot be marked trusted`;
  }
  return null;
}

export function isRetiredBeerSource(source) {
  return source?.monitoringStatus === 'retired';
}

export function excludeRetiredBeerSources(sources = []) {
  return sources.filter((source) => !isRetiredBeerSource(source));
}

export function listedBeerIngestionSources(sources = []) {
  return excludeRetiredBeerSources(sources).filter(isIngestionPipeSource);
}

export function canTrustBeerSource(source) {
  return getBeerSourceTrustBlockReason(source) === null;
}

export function getBeerSourceCategoryChipProps(category) {
  if (category === 'aggregator') {
    return {
      color: 'warning',
      variant: 'filled',
      label: 'Aggregator',
    };
  }
  if (category === 'official') {
    return {
      color: 'primary',
      variant: 'outlined',
      label: 'Official',
    };
  }
  return {
    color: 'default',
    variant: 'outlined',
    label: formatKebabLabel(category || 'unknown'),
  };
}

export function getBeerIngestionStatusChipColor(status) {
  if (status === 'trusted') {
    return 'success';
  }
  if (status === 'needs_source') {
    return 'warning';
  }
  if (status === 'no_suitable_source') {
    return 'info';
  }
  return 'default';
}

export function rowHasWebsite(row) {
  return Boolean(row?.website && String(row.website).trim());
}

export function findTrustableSource(sources = []) {
  return (
    listedBeerIngestionSources(sources).find(
      (source) => canTrustBeerSource(source) && source.isTrusted !== true,
    ) ?? null
  );
}

export function getAddSourcePrefillUrl(row) {
  if (listedBeerIngestionSources(row?.sources).length > 0) {
    return '';
  }
  return typeof row?.website === 'string' ? row.website.trim() : '';
}

export function getRowTrustActionState(row) {
  if (!row || row.derivedStatus === 'excluded') {
    return {
      enabled: false,
      sourceId: null,
      reason: 'Excluded breweries cannot be marked trusted',
    };
  }
  if (row.derivedStatus === 'trusted') {
    return {
      enabled: false,
      sourceId: row.trustedSource?.id ?? null,
      reason: 'Already has a trusted official source',
    };
  }
  const sources = listedBeerIngestionSources(row.sources);
  if (sources.length === 0) {
    return {
      enabled: false,
      sourceId: null,
      reason: 'Add a source first',
    };
  }
  const trustableSource = findTrustableSource(sources);
  if (trustableSource) {
    return {
      enabled: true,
      sourceId: trustableSource.id,
      reason: null,
    };
  }
  return {
    enabled: false,
    sourceId: null,
    reason: getBeerSourceTrustBlockReason(sources[0]),
  };
}

export function canMarkNoSuitableSource(row) {
  return row?.derivedStatus === 'needs_source';
}

export function isSafeExternalUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

export function formatUrlHostname(url) {
  if (!url || typeof url !== 'string') {
    return '';
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return '';
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return trimmed;
    }
    const hostname = parsed.hostname.replace(/^www\./i, '');
    return hostname || trimmed;
  } catch {
    return trimmed;
  }
}

export function filterBeerIngestionRows(
  rows,
  {
    status = BEER_INGESTION_STATUS_FILTER_ALL,
    hasWebsite = false,
    showExcluded = false,
    monitoringFilter = BEER_INGESTION_MONITORING_FILTER_ALL,
  } = {},
) {
  return (rows || []).filter((row) => {
    if (status === BEER_INGESTION_STATUS_FILTER_ALL) {
      if (row.derivedStatus === 'excluded' && !showExcluded) {
        return false;
      }
    } else if (row.derivedStatus !== status) {
      return false;
    }

    if (!matchesBeerIngestionMonitoringFilter(row, monitoringFilter)) {
      return false;
    }

    return !hasWebsite || rowHasWebsite(row);
  });
}

export function isBeerIngestionTargetMet(
  summary,
  targetPercent = BEER_INGESTION_TRUST_TARGET_PERCENT,
) {
  return (summary?.trustedPercent ?? 0) >= targetPercent;
}

export function formatBeerIngestionProgress(
  summary,
  targetPercent = BEER_INGESTION_TRUST_TARGET_PERCENT,
) {
  const trusted = summary?.trusted ?? 0;
  const increment1Denominator = summary?.increment1Denominator ?? 0;
  const trustedPercent = summary?.trustedPercent ?? 0;
  const increment1ExceptionCount = summary?.increment1ExceptionCount ?? 0;
  return (
    `Increment 1 trusted official sources: ${trusted} / ${increment1Denominator}`
    + ` (${trustedPercent}%) — target ${targetPercent}%.`
    + ` 1b exceptions excluded from bar: ${increment1ExceptionCount}.`
  );
}

export function formatBeerIngestionPilotWindowLabel() {
  return 'Pilot window: 30 Aug 2026 – 27 Sep 2026 (week 4 ends 27 Sep 2026)';
}

export function formatBeerIngestionMedianReview(medianReviewMinutes) {
  if (medianReviewMinutes === null || medianReviewMinutes === undefined) {
    return 'n/a — not enough completed reviews.';
  }
  return `Median review time: ${medianReviewMinutes} minutes`;
}
